'use client'

import React, { ReactNode, forwardRef } from 'react'

import { Box, HTMLChakraProps } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'
import { CredentialsIcon } from '../Icon/CredentialsIcon'
import { UserIcon } from '../Icon/UserIcon'
import {
  TraceLogEventVariant,
  traceLogEventIconColor,
  traceLogEventStyles,
} from './TraceLogEvent.styles'

export type { TraceLogEventVariant }

export type TraceLogEventProps = Omit<HTMLChakraProps<'div'>, 'title'> & {
  /** Event name, e.g. "Forced Entry" */
  title: string
  /** Preformatted timestamp, e.g. "08:46:09 | 06/29/2026" */
  timestamp: string
  /** `alarm` renders the row in the error status color */
  variant?: TraceLogEventVariant
  /** Card holder row, rendered with the user icon. Omit to hide. */
  cardHolderName?: string
  /** Badge/credential row, rendered with the credentials icon. Omit to hide. */
  badgeNumber?: string
  /** Device row label. Omit to hide. */
  deviceName?: string
  /** Leading icon for the device row */
  deviceIcon?: ReactNode
  /** Inline callout (e.g. a compact Alert) rendered above the title row */
  callout?: ReactNode
  colorScheme?: ColorScheme
}

/**
 * A single trace-log event row: title + monospace timestamp, with optional
 * card holder / badge / device detail rows and an optional inline callout.
 * Rows separate with a top hairline rather than a card border.
 *
 * @see {@link https://www.figma.com/design/E5CTSI1t3TwBt1CwEYZ3JM/Product?node-id=7806-300 Product · Trace Logs}
 */
export const TraceLogEvent = forwardRef<HTMLDivElement, TraceLogEventProps>(
  (
    {
      title,
      timestamp,
      variant = 'default',
      cardHolderName,
      badgeNumber,
      deviceName,
      deviceIcon,
      callout,
      colorScheme = 'dark',
      ...rest
    },
    ref
  ) => {
    const s = traceLogEventStyles(variant, colorScheme)
    const iconColor = traceLogEventIconColor(variant, colorScheme)

    return (
      <Box ref={ref} {...s.container} {...rest}>
        {callout}
        <Box {...s.titleRow}>
          <Box {...s.title}>{title}</Box>
          <Box {...s.timestamp}>{timestamp}</Box>
        </Box>
        {cardHolderName && (
          <Box {...s.detailRow}>
            <UserIcon color={iconColor} size={16} />
            <Box {...s.detailLabel}>{cardHolderName}</Box>
          </Box>
        )}
        {badgeNumber && (
          <Box {...s.detailRow}>
            <CredentialsIcon color={iconColor} size={16} />
            <Box {...s.accentLabel}>{badgeNumber}</Box>
          </Box>
        )}
        {deviceName && (
          <Box {...s.detailRow}>
            {deviceIcon}
            <Box {...s.detailLabel}>{deviceName}</Box>
          </Box>
        )}
      </Box>
    )
  }
)

TraceLogEvent.displayName = 'TraceLogEvent'
